// AM Advertising Home — what the pulse cards PLOT. PURE + tested.
//
// The selector is metric (Spend · Leads · CPL) × channel (Blended · Meta ·
// Google), and ONE selection drives all three cards — they are the same
// question over three windows, so three independent selectors would be noise.
//
// ⚠️⚠️ CPL IS RE-DERIVED PER BUCKET — that day's spend ÷ that day's leads —
// NEVER an average of daily CPLs (the do-NOT-average-a-ratio rule the whole
// reporting layer runs on). A bucket with no leads plots as a GAP, never as a
// zero: a $0.00 cost-per-lead on a day that produced none is a lie the chart
// would tell confidently.
//
// ⚠️ Money goes through the SAME display-currency machinery as everywhere: a
// converted blend when a display currency is active, and in Native mode the
// single dominant currency's own figures — never a raw cross-currency sum,
// which a single-series chart could not honestly draw anyway.

import { moneySeriesValue } from '../reporting/currencyConversion.js';
import type { FxRateSnapshot } from '../../types/fxRates.js';
import type { CurrencyAmount } from '../../types/reporting.js';
import type { PulseBucket } from './pulse.js';

export type PulseMetric = 'spend' | 'leads' | 'cpl';
export type PulseChannelKey = 'blended' | 'meta' | 'google';

export const PULSE_METRIC_OPTIONS: Array<{ value: PulseMetric; label: string }> = [
  { value: 'spend', label: 'Spend' },
  { value: 'leads', label: 'Leads' },
  { value: 'cpl', label: 'CPL' },
];

export const PULSE_CHANNEL_OPTIONS: Array<{ value: PulseChannelKey; label: string }> = [
  { value: 'blended', label: 'Blended' },
  { value: 'meta', label: 'Meta' },
  { value: 'google', label: 'Google' },
];

export function isPulseMetric(v: string): v is PulseMetric {
  return PULSE_METRIC_OPTIONS.some((o) => o.value === v);
}
export function isPulseChannel(v: string): v is PulseChannelKey {
  return PULSE_CHANNEL_OPTIONS.some((o) => o.value === v);
}

/** Merge per-currency spend across the channels a selection covers. */
function spendFor(bucket: PulseBucket, channel: PulseChannelKey): CurrencyAmount[] {
  if (channel === 'meta') return bucket.meta.spend;
  if (channel === 'google') return bucket.google.spend;
  const m = new Map<string, number>();
  for (const a of [...bucket.meta.spend, ...bucket.google.spend]) {
    m.set(a.currency, (m.get(a.currency) ?? 0) + a.amount);
  }
  return [...m.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }));
}

function leadsFor(bucket: PulseBucket, channel: PulseChannelKey): number {
  if (channel === 'meta') return bucket.meta.leads;
  if (channel === 'google') return bucket.google.leads;
  return Math.round((bucket.meta.leads + bucket.google.leads) * 100) / 100;
}

/**
 * The plotted value for one bucket. `null` = a genuine gap (no bar), which is
 * reserved for the RATIO metric — additive metrics report a real 0 for a day
 * with no activity.
 */
export function pulseBucketValue(
  bucket: PulseBucket,
  metric: PulseMetric,
  channel: PulseChannelKey,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
  nativeCurrency: string | null,
): number | null {
  const leads = leadsFor(bucket, channel);
  if (metric === 'leads') return leads;

  const spend = spendFor(bucket, channel);
  const spendValue = spend.length === 0 ? 0 : moneySeriesValue(spend, display, snapshot, nativeCurrency);

  if (metric === 'spend') return spendValue;
  // CPL — re-derived from THIS bucket's own spend and leads.
  if (leads <= 0 || spendValue === null) return null;
  return Math.round((spendValue / leads) * 100) / 100;
}

/**
 * The currency a money series is drawn in — the display currency when one is
 * active, else the book's dominant native currency. Null for count metrics.
 */
export function pulseSeriesCurrency(
  metric: PulseMetric,
  display: string,
  nativeCurrency: string | null,
): string | null {
  if (metric === 'leads') return null;
  return display === 'native' ? nativeCurrency : display;
}

export function pulseMetricLabel(metric: PulseMetric, channel: PulseChannelKey): string {
  const m = PULSE_METRIC_OPTIONS.find((o) => o.value === metric)?.label ?? metric;
  const c = PULSE_CHANNEL_OPTIONS.find((o) => o.value === channel)?.label ?? channel;
  return `${c} ${m.toLowerCase()}`;
}
